/**
 * "Refresh sentiment scan" — the Public Sentiment rerun's one explicit choice
 * about its evidence (owner decision 2, 2026-09-30).
 *
 * By default the rerun reuses the site evidence's sentiment scan while it is
 * under the shared 7-day limit (sentimentScanAge.ts); the report states that
 * scan's collection date. Ticking this asks the enqueue to re-read the
 * external sources first (`siteConfigOverrides.refreshPublicSentimentScan`).
 * A short confirm says what that does. When the re-read finds nothing new,
 * the saved scan, and the site evidence version, are kept.
 *
 * Mounted by FeasibilityRerunPanel (RerunWrapper's default branch serves
 * Public Sentiment), itself mounted from the /sites/:siteId/reports/:reportId/rerun
 * route through App → SiteRagEngine → SiteRagReportView → RerunWrapper.
 */
import React from 'react';
import { openConfirmDialog } from '../../ui/confirmDialog';
import { PUBLIC_SENTIMENT_SCAN_MAX_AGE_DAYS } from '../../../lib/sentiment/sentimentScanAge';

export interface SentimentScanRefreshOptionProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** `publicSentiment.fetchedAt` of the site's current SED, when loaded. */
  scanFetchedAt?: string | null;
  disabled?: boolean;
  /** Test seam for the confirm; production uses the shared dialog. */
  confirm?: typeof openConfirmDialog;
  /** Test seam for "now". */
  now?: () => number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function scanDateLabel(fetchedAt: string | null | undefined, nowMs: number): string | null {
  const at = fetchedAt ? Date.parse(fetchedAt) : Number.NaN;
  if (!Number.isFinite(at)) return null;
  const date = new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const days = Math.max(0, Math.floor((nowMs - at) / DAY_MS));
  return `${date} (${days === 0 ? 'today' : days === 1 ? '1 day old' : `${days} days old`})`;
}

export function SentimentScanRefreshOption({
  checked,
  onChange,
  scanFetchedAt,
  disabled = false,
  confirm = openConfirmDialog,
  now = Date.now,
}: SentimentScanRefreshOptionProps): React.ReactElement {
  const scanDate = scanDateLabel(scanFetchedAt, now());
  const handleChange = async (next: boolean) => {
    if (!next) {
      onChange(false);
      return;
    }
    const ok = await confirm({
      title: 'Refresh the sentiment scan?',
      body: (
        <>
          <p>
            This re-reads the external public sources (news, federal dockets, meeting minutes)
            instead of reusing the saved scan{scanDate ? ` from ${scanDate}` : ''}. It adds about a minute.
          </p>
          <p>If they return nothing new, the saved scan is kept.</p>
        </>
      ),
      confirmLabel: 'Refresh on rerun',
      cancelLabel: 'Keep saved scan',
    });
    onChange(ok);
  };
  return (
    <label
      className="flex items-start gap-2 rounded-md border border-border bg-surface-1 px-3 py-2"
      data-testid="rerun-refresh-sentiment-scan"
    >
      <input
        type="checkbox"
        className="mt-0.5 h-3.5 w-3.5 accent-primary"
        checked={checked}
        disabled={disabled}
        onChange={(event) => { void handleChange(event.target.checked); }}
        data-testid="rerun-refresh-sentiment-scan-checkbox"
      />
      <span className="min-w-0">
        <span className="block type-meta font-semibold text-text-primary">Refresh sentiment scan</span>
        <span className="block type-body text-text-muted" data-testid="rerun-refresh-sentiment-scan-hint">
          {checked
            ? 'The rerun re-reads the external sources first.'
            : scanDate
              ? `Reuses the saved scan from ${scanDate}.`
              : `Reuses the saved scan while it is under ${PUBLIC_SENTIMENT_SCAN_MAX_AGE_DAYS} days old.`}
        </span>
      </span>
    </label>
  );
}
